import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Bot, Search, Megaphone, FileText, LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { ScrollProgress } from "@/components/ui/ScrollProgress";
import { TextReveal } from "@/components/ui/TextReveal";

import { ParallaxCard } from "@/components/ui/ParallaxCard";
import { AnimatedIcon } from "@/components/ui/AnimatedIcon";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { FloatingShapes } from "@/components/ui/GlowingOrb";
import { SEO } from "@/components/SEO";

// Icon mapping for each service
const serviceIcons = {
  geo: "search" as const,
  "ai-ads": "megaphone" as const,
  "ai-content": "file" as const,
  "ai-agents": "bot" as const,
};

interface ServiceData {
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  animatedIcon: "bot" | "search" | "megaphone" | "file";
  problem: string;
  solution: string;
  features: string[];
  process: { step: string; title: string; description: string }[];
  stats: { value: number; suffix: string; label: string }[];
  pricing: string;
}

const servicesData: Record<string, ServiceData> = {
  geo: {
    title: "GEO",
    subtitle: "Generative Engine Optimization",
    description: "Get discovered by ChatGPT, Claude, Perplexity, and other AI search engines. The new frontier of search visibility.",
    icon: Search,
    animatedIcon: "search",
    problem: "Traditional SEO only gets you into Google. But 40% of users now start their searches with AI assistants. If you're not optimized for AI, you're invisible to a growing audience.",
    solution: "GEO ensures your brand appears in AI-generated answers. We structure your content, build citations, and optimize for how LLMs understand and recommend businesses.",
    features: [
      "AI search visibility audits",
      "Content structuring for LLMs",
      "Citation and mention building",
      "Entity optimization",
      "Monthly AI rankings tracking",
      "Competitor AI visibility analysis",
    ],
    process: [
      { step: "01", title: "Audit", description: "Analyze your current AI search visibility" },
      { step: "02", title: "Strategy", description: "Develop GEO optimization plan" },
      { step: "03", title: "Optimize", description: "Implement content and citation changes" },
      { step: "04", title: "Monitor", description: "Track and improve AI rankings" },
    ],
    stats: [
      { value: 312, suffix: "%", label: "Increase in AI Mentions" },
      { value: 45, suffix: "%", label: "New Traffic from AI" },
      { value: 8, suffix: "x", label: "Citation Growth" },
    ],
    pricing: "$2,000",
  },
  "ai-ads": {
    title: "AI-Powered Ads",
    subtitle: "Intelligent Paid Media",
    description: "Smarter campaigns on Google, Meta, and LinkedIn powered by AI creative generation and predictive optimization.",
    icon: Megaphone,
    animatedIcon: "megaphone",
    problem: "Traditional paid ads are expensive and inefficient. Manual A/B testing is slow. Creative fatigue kills performance. You're spending more for fewer results.",
    solution: "AI transforms every aspect of paid media. From generating high-performing ad creatives to predicting which audiences will convert, we make every ad dollar work harder.",
    features: [
      "AI-generated ad creatives",
      "Predictive audience targeting",
      "Automated bid optimization",
      "Dynamic creative testing",
      "Cross-platform attribution",
      "Real-time performance alerts",
    ],
    process: [
      { step: "01", title: "Analyze", description: "Audit current campaigns and audiences" },
      { step: "02", title: "Create", description: "Generate AI-powered creatives" },
      { step: "03", title: "Launch", description: "Deploy optimized campaigns" },
      { step: "04", title: "Scale", description: "Continuously improve with AI" },
    ],
    stats: [
      { value: 40, suffix: "%", label: "Lower CPA" },
      { value: 3, suffix: "x", label: "ROAS Improvement" },
      { value: 10, suffix: "x", label: "Creative Variations" },
    ],
    pricing: "$3,000",
  },
  "ai-content": {
    title: "AI Content & SEO",
    subtitle: "Content at Scale",
    description: "Scale content production 10x while maintaining quality, brand voice, and search rankings.",
    icon: FileText,
    animatedIcon: "file",
    problem: "Content creation is expensive and slow. Hiring writers doesn't scale. Quality varies. You can't produce enough content to compete with larger players.",
    solution: "AI-assisted content creation multiplies your output without sacrificing quality. We combine AI efficiency with human expertise to create content that ranks and converts.",
    features: [
      "AI-assisted content creation",
      "Brand voice training",
      "SEO optimization at scale",
      "Topic cluster strategy",
      "Content performance analytics",
      "Automated publishing workflows",
    ],
    process: [
      { step: "01", title: "Research", description: "Identify high-value topics and keywords" },
      { step: "02", title: "Create", description: "Produce AI-assisted content" },
      { step: "03", title: "Optimize", description: "SEO and brand voice refinement" },
      { step: "04", title: "Publish", description: "Distribute and track performance" },
    ],
    stats: [
      { value: 10, suffix: "x", label: "Content Output" },
      { value: 156, suffix: "%", label: "Organic Traffic Growth" },
      { value: 60, suffix: "%", label: "Cost Savings" },
    ],
    pricing: "$2,500",
  },
  "ai-agents": {
    title: "AI Agents & Automation",
    subtitle: "Intelligent Automation",
    description: "Custom AI chatbots, lead qualification bots, and workflow automation that work 24/7.",
    icon: Bot,
    animatedIcon: "bot",
    problem: "You're losing leads outside business hours. Your team spends too much time on repetitive tasks. Customer response times are too slow. Scaling support is expensive.",
    solution: "AI agents handle the work your team shouldn't. From qualifying leads to answering customer questions, our custom bots work around the clock so your team can focus on high-value activities.",
    features: [
      "Custom AI chatbots",
      "Lead qualification automation",
      "Customer support agents",
      "CRM and tool integrations",
      "Conversation analytics",
      "Continuous training and improvement",
    ],
    process: [
      { step: "01", title: "Discovery", description: "Understand your workflows and needs" },
      { step: "02", title: "Build", description: "Develop custom AI agent" },
      { step: "03", title: "Train", description: "Fine-tune on your data" },
      { step: "04", title: "Deploy", description: "Launch and monitor performance" },
    ],
    stats: [
      { value: 24, suffix: "/7", label: "Availability" },
      { value: 80, suffix: "%", label: "Queries Automated" },
      { value: 5, suffix: "x", label: "Response Speed" },
    ],
    pricing: "$2,000",
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = servicesData[slug || "geo"];

  if (!service) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Service not found</h1>
            <Link to="/services">
              <Button>View All Services</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const Icon = service.icon;

  return (
    <Layout>
      <SEO 
        title={`${service.title} - ${service.subtitle}`}
        description={service.description}
        canonical={`https://thecloudseo.com/services/${slug}`}
      />
      <ScrollProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <AnimatedBackground variant="mesh" />
        <FloatingShapes />
        
        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.8 }}
            >
              <motion.div 
                className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-8"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Icon className="w-8 h-8" />
              </motion.div>
              <TextReveal>
                <p className="text-lg text-muted-foreground mb-4">{service.subtitle}</p>
              </TextReveal>
              <TextReveal delay={0.1}>
                <h1 className="heading-hero mb-6">{service.title}</h1>
              </TextReveal>
              <TextReveal delay={0.2}>
                <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mb-10">
                  {service.description}
                </p>
              </TextReveal>
              <motion.div 
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="rounded-full px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90 btn-magnetic"
                  >
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" size="lg" className="rounded-full px-8 hover-lift">
                    From {service.pricing}/mo
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
            
            <ParallaxCard className="rounded-3xl overflow-hidden bg-secondary/30 border border-border/50" intensity={8}>
              <div className="aspect-square flex items-center justify-center p-8">
                <AnimatedIcon 
                  icon={service.animatedIcon}
                  size="lg"
                  className="w-full h-full"
                />
              </div>
            </ParallaxCard>
          </div>
        </div>
      </section>

      {/* Problem/Solution */}
      <section className="section-spacing bg-secondary/30 relative overflow-hidden">
        <AnimatedBackground variant="grid" />
        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-12">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-3xl bg-destructive/5 border border-destructive/20"
              >
                <h3 className="text-sm font-semibold text-destructive uppercase tracking-wider mb-4">
                  The Problem
                </h3>
                <p className="text-xl md:text-2xl">{service.problem}</p>
              </motion.div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-8 rounded-3xl bg-primary/5 border border-primary/20"
              >
                <h3 className="text-sm font-semibold text-primary uppercase tracking-wider mb-4">
                  Our Solution
                </h3>
                <p className="text-xl md:text-2xl">{service.solution}</p>
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-spacing relative">
        <AnimatedBackground variant="dots" />
        <div className="container-wide relative z-10">
          <AnimatedSection className="max-w-3xl mb-16">
            <TextReveal>
              <h2 className="heading-section mb-6">What's Included</h2>
            </TextReveal>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <ParallaxCard 
                  className="p-6 rounded-2xl bg-secondary/30 h-full hover-glow"
                  intensity={3}
                >
                  <div className="flex items-start gap-4">
                    <motion.div 
                      className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0"
                      whileHover={{ scale: 1.2 }}
                    >
                      <Check className="w-4 h-4 text-primary-foreground" />
                    </motion.div>
                    <span className="text-lg">{feature}</span>
                  </div>
                </ParallaxCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing bg-secondary/30 relative overflow-hidden">
        <FloatingShapes />
        <div className="container-wide relative z-10">
          <AnimatedSection className="max-w-3xl mb-16">
            <TextReveal>
              <h2 className="heading-section mb-6">Our Process</h2>
            </TextReveal>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <motion.div
                  className="text-7xl font-bold mb-4 gradient-text-animate"
                  whileHover={{ scale: 1.05 }}
                >
                  {step.step}
                </motion.div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-spacing bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 opacity-10"
          animate={{ 
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center mb-16">
            <TextReveal>
              <h2 className="heading-section mb-4">Results We Deliver</h2>
            </TextReveal>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-12">
            {service.stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ 
                  type: "spring", 
                  stiffness: 100, 
                  delay: index * 0.15 
                }}
              >
                <div className="text-5xl md:text-6xl lg:text-7xl font-bold mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-primary-foreground/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing relative overflow-hidden">
        <FloatingShapes />
        <div className="container-narrow text-center relative z-10">
          <AnimatedSection>
            <TextReveal>
              <h2 className="heading-section mb-6">
                Ready to Get Started?
              </h2>
            </TextReveal>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Book a free strategy call to learn how {service.title} can transform your marketing.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="rounded-full px-10 text-base h-14 bg-primary text-primary-foreground hover:bg-primary/90 btn-magnetic"
              >
                Book a Strategy Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
